import React, { useState, useEffect, useRef, useMemo } from 'react';
import { geoMercator, geoPath as d3GeoPath } from 'd3-geo';
import { scaleLinear } from 'd3-scale';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import { STATE_CODE_MAP, GAME_YEAR, formatUSD, PARTY_COLORS } from './constants';
import { useContainerSize } from './hooks';
import PoliticoHoverCard from './PoliticoHoverCard';

export default function GameCityMap({ stateSigla, stateName, onSelect, onBack, prefeitos, playerAvatar }) {
  const [geoData, setGeoData] = useState(null);
  const [municipalities, setMunicipalities] = useState([]);
  const [popMunMap, setPopMunMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [disabledParties, setDisabledParties] = useState(new Set());
  const [showLegend, setShowLegend] = useState(true);
  const containerRef = useRef(null);
  const { width: cw, height: ch } = useContainerSize(containerRef);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const stateCode = STATE_CODE_MAP[stateSigla.toUpperCase()];
    if (!stateCode) return;

    Promise.all([
      axios.get(`/geodata/${stateCode}`),
      axios.get(`/comex/state-municipalities/${GAME_YEAR}/${stateSigla.toUpperCase()}`),
      axios.get(`/populacao/estado/${stateSigla.toUpperCase()}`).catch(() => null),
    ])
      .then(([geoRes, munRes, popRes]) => {
        if (!alive) return;
        setGeoData(geoRes.data);
        if (munRes.data?.success) setMunicipalities(munRes.data.data || []);
        if (popRes?.data?.success) {
          const pm = {};
          (popRes.data.data.municipios || []).forEach(m => { pm[String(m.codigoIbge)] = m.populacao; });
          setPopMunMap(pm);
        }
      })
      .catch((err) => console.error('Erro ao carregar mapa:', err))
      .finally(() => alive && setLoading(false));

    return () => { alive = false; };
  }, [stateSigla]);

  const munMap = useMemo(() => {
    const m = {};
    municipalities.forEach((mun) => { m[String(mun.codigo)] = mun; });
    return m;
  }, [municipalities]);

  const popColorScale = useMemo(() => {
    const vals = Object.values(popMunMap);
    if (vals.length === 0) return null;
    const maxPop = Math.max(...vals, 1);
    return scaleLinear()
      .domain([0, maxPop * 0.01, maxPop * 0.05, maxPop * 0.2, maxPop])
      .range(['#1a1a2e', '#16213e', '#0f3460', '#e94560', '#ff6b6b']);
  }, [popMunMap]);

  const containerH = Math.max(400, ch || 400);
  const containerW = Math.max(400, cw || 600);

  const { svgW, svgH, pathGen } = useMemo(() => {
    if (!geoData || containerW < 100) return { svgW: 0, svgH: 0, pathGen: null };
    const pad = 30;
    const proj = geoMercator().fitExtent(
      [[pad, pad], [containerW - pad, containerH - pad]],
      geoData
    );
    const pg = d3GeoPath().projection(proj);
    return { svgW: containerW, svgH: containerH, pathGen: pg };
  }, [geoData, containerW, containerH]);

  const partyStats = useMemo(() => {
    const counts = {};
    const prefValues = Object.values(prefeitos || {});
    for (const p of prefValues) {
      if (p?.partido) counts[p.partido] = (counts[p.partido] || 0) + 1;
    }
    return Object.entries(counts)
      .map(([partido, count]) => ({ partido, count, color: PARTY_COLORS[partido] || '#888' }))
      .sort((a, b) => b.count - a.count);
  }, [prefeitos]);

  const toggleParty = (partido) => {
    setDisabledParties(prev => {
      const next = new Set(prev);
      if (next.has(partido)) next.delete(partido);
      else next.add(partido);
      return next;
    });
  };

  const toggleAll = () => {
    if (disabledParties.size === 0) {
      setDisabledParties(new Set(partyStats.map(p => p.partido)));
    } else {
      setDisabledParties(new Set());
    }
  };

  const formatPopShort = (val) => {
    if (val >= 1e6) return `${(val / 1e6).toFixed(1)}M`;
    if (val >= 1e3) return `${(val / 1e3).toFixed(0)}mil`;
    return String(val);
  };

  if (loading) {
    return (
      <div className="city-map__loading">
        <div className="city-map__spinner" />
        <p className="city-map__loading-text">Carregando mapa de {stateName}...</p>
      </div>
    );
  }

  const hoveredName = hovered && geoData
    ? (geoData.features || []).find((f) => String(f.properties?.id) === hovered)?.properties?.name
    : null;
  const hoveredMun = hovered ? munMap[hovered] : null;
  const hoveredPop = hovered ? popMunMap[hovered] : null;

  const hoveredPrefeito = hoveredName
    ? prefeitos[hoveredName.toUpperCase()] || prefeitos[hoveredName]
    : null;

  const features = geoData?.features || [];

  return (
    <div className="city-map">
      <div className="city-map__header">
        <button onClick={onBack} className="city-map__back-btn">
          <ArrowLeft size={20} />
        </button>
        {playerAvatar && (
          <div className="city-map__avatar" dangerouslySetInnerHTML={{ __html: playerAvatar.svg }} />
        )}
        <h2 className="city-map__title">
          Escolha seu Município em {stateName}
        </h2>
      </div>
      <p className="city-map__subtitle">
        {features.length} municípios — cores indicam o tamanho da população
      </p>

      {popColorScale && (
        <div className="city-map__legend">
          <span className="city-map__legend-label">Menor</span>
          <div className="city-map__legend-bar" />
          <span className="city-map__legend-label">Maior pop.</span>
        </div>
      )}

      {/* Party filter legend */}
      {partyStats.length > 0 && (
        <div className="city-map__party-legend">
          <div className="city-map__party-legend-header">
            <button onClick={() => setShowLegend(!showLegend)} className="city-map__party-legend-toggle">
              Partidos ({partyStats.length})
              <span className="city-map__party-legend-arrow">{showLegend ? '\u25B2' : '\u25BC'}</span>
            </button>
            {showLegend && (
              <button onClick={toggleAll} className="city-map__party-legend-all">
                {disabledParties.size === 0 ? 'Ocultar todos' : 'Mostrar todos'}
              </button>
            )}
          </div>
          {showLegend && (
            <div className="city-map__party-legend-grid">
              {partyStats.map(({ partido, count, color }) => {
                const isActive = !disabledParties.has(partido);
                return (
                  <button
                    key={partido}
                    onClick={() => toggleParty(partido)}
                    className={`city-map__party-chip ${isActive ? 'city-map__party-chip--active' : 'city-map__party-chip--inactive'}`}
                    style={{
                      borderColor: isActive ? color : 'rgba(0,0,0,.1)',
                      background: isActive ? `${color}55` : 'rgba(0,0,0,.04)',
                    }}
                  >
                    <span className="city-map__party-chip-dot" style={{ background: isActive ? color : '#999' }} />
                    <span className="city-map__party-chip-name" style={{ color: isActive ? '#111' : '#999' }}>{partido}</span>
                    <span className="city-map__party-chip-count" style={{ color: isActive ? '#333' : '#999' }}>{count}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div ref={containerRef} className="city-map__container"
        onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}>
        {pathGen && svgW > 0 && (
          <svg
            viewBox={`0 0 ${svgW} ${svgH}`}
            preserveAspectRatio="xMidYMid meet"
            style={{ width: '100%', height: '100%' }}
          >
            {features.map((feat, i) => {
              const code = String(feat.properties?.id || '');
              const mun = munMap[code];
              const pop = popMunMap[code];
              const isHovered = hovered === code;
              const d = pathGen(feat);
              if (!d) return null;
              const baseFill = pop && popColorScale ? popColorScale(pop) : '#1e2d4a';
              return (
                <path
                  key={code || i}
                  d={d}
                  fill={isHovered ? '#ffffff' : baseFill}
                  stroke={isHovered ? '#e94560' : '#0a0e1a'}
                  strokeWidth={isHovered ? 1.5 : 0.5}
                  cursor="pointer"
                  style={{ transition: 'fill 0.15s, stroke 0.15s' }}
                  onMouseEnter={() => setHovered(code)}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => {
                    const name = feat.properties?.name || 'Município';
                    onSelect(code, name, mun || { codigo: code, export: 0, import: 0, balance: 0 });
                  }}
                />
              );
            })}
            {/* Party badges on municipalities */}
            {features.map((feat, i) => {
              const name = feat.properties?.name;
              if (!name) return null;
              const pref = prefeitos[name.toUpperCase()] || prefeitos[name];
              if (!pref?.partido) return null;
              if (disabledParties.has(pref.partido)) return null;
              const centroid = pathGen.centroid(feat);
              if (!centroid || isNaN(centroid[0])) return null;
              const partyColor = PARTY_COLORS[pref.partido] || '#888';
              const pop = popMunMap[String(feat.properties?.id || '')];
              const isLarge = pop && pop > 100000;
              const r = isLarge ? 6 : 3.5;
              const fontSize = isLarge ? 5 : 0;
              return (
                <g key={`badge-${i}`} transform={`translate(${centroid[0]},${centroid[1]})`} pointerEvents="none">
                  <circle r={r} fill={partyColor} stroke="#0a0e1a" strokeWidth={0.6} opacity={0.85} />
                  {isLarge && fontSize > 0 && (
                    <text
                      textAnchor="middle" dominantBaseline="central"
                      className="state-map__party-text"
                      style={{ fontSize: `${fontSize}px` }}
                    >
                      {pref.partido.length > 3 ? pref.partido.slice(0, 3) : pref.partido}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        )}
      </div>

      {hoveredName && !hoveredPrefeito && (
        <div className="city-map__hover-info">
          <p className="city-map__hover-name">{hoveredName}</p>
          {hoveredPop ? (
            <p className="city-map__hover-pop">
              População: {new Intl.NumberFormat('pt-BR').format(hoveredPop)}
            </p>
          ) : hoveredMun && (
            <p className="city-map__hover-pop">
              Exp: {formatUSD(hoveredMun.export)} | Imp: {formatUSD(hoveredMun.import)}
            </p>
          )}
        </div>
      )}

      {hoveredPrefeito && (
        <PoliticoHoverCard
          politico={hoveredPrefeito}
          label={`Prefeito${hoveredPrefeito.genero === 'FEMININO' ? 'a' : ''} — ${hoveredName}`}
          position={mousePos}
          extraInfo={hoveredPop ? `Pop: ${formatPopShort(hoveredPop)}` : null}
        />
      )}
    </div>
  );
}
